import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useParallaxY } from "../../hooks/useParallaxY";
import AnimatedText from "../AnimatedText";
import CreativeButton from "../CreativeButton";

type BrandItem = {
  title: string;
  desc: string;
  image: string;
};

const brandData: Record<string, BrandItem[]> = {
  liebherr: [
    {
      title: "Chest Type - Hard Top",
      desc: "Built for heavy-duty performance and bulk storage. Features solid insulated lids for maximum energy efficiency and long-lasting temperature stability, ideal for supermarkets and food service.",
      image: "/images/products/efl4605top.webp"
    },
    {
      title: "Chest Type - Glass Lid",
      desc: "Combines professional presentation with advanced cooling. Smooth-sliding glass lids offer excellent insulation and visibility, providing a dependable solution for both storage and product display.",
      image: "/images/products/efe3802top.webp"
    },
    {
      title: "Upright Freezers",
      desc: "Space-efficient designs featuring organized interiors and automatic defrost. Equipped with LED lighting for branding and visibility, these units ensure premium product preservation.",
      image: "/images/products/fdv4613open.webp"
    },
    {
      title: "Supermarket Line",
      desc: "German-engineered multi-door systems and chest freezers designed to optimize store layouts. These solutions increase product visibility while reducing operating costs and ensuring daily reliability.",
      image: "/images/products/sgt1172top.webp"
    },
  ],
  arctique: [
    // Hidden for now along with the blast freezer products (see data/hiddenProducts.ts).
    // {
    //   title: "BLAST FREEZER",
    //   desc: "Specially engineered for rapid freezing and recharging eutectic plates used in cold chain logistics. Ensures extended cooling performance during transport without continuous power supply.",
    //   image: "/images/products/blastfreezer.webp"
    // },
    {
      title: "ARTISAN BAKERY SERIES",
      desc: "Flexible chiller/freezer units in stainless steel. Optimized for dough and ingredient preservation with precise texture control, preparing your bakery for preparation without quality loss.",
      image: "/images/products/artisanbakery.webp"
    },
    {
      title: "Supermarket Line",
      desc: "Versatile open-front meat and produce displays. Features night curtains for energy savings and optional fan cooling for consistent, accessible fresh-product storage in modern retail stores.",
      image: "/images/products/supermarket.webp"
    },
    {
      title: "Upright Line",
      desc: "Commercial-grade displays with customizable canopies for branding. Anti-fog glass and bright LED lighting create a premium showcase for high-traffic supermarkets and convenience stores.",
      image: "/images/products/upright.webp"
    },
  ],
};

const Brands: React.FC = () => {
  const navigate = useNavigate();
  const brandSectionRef = useRef<HTMLElement>(null);
  const brandBlobY1 = useParallaxY(brandSectionRef, [-90, 90]);
  const brandBlobY2 = useParallaxY(brandSectionRef, [90, -90]);

  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [brandCatalogIndex, setBrandCatalogIndex] = useState(0);
  const [hoveredBrand, setHoveredBrand] = useState<string | null>(null);

  return (
    <motion.section
      ref={brandSectionRef}
      id="brands"
      layout
      className="relative bg-gradient-to-b from-white via-[#e6eef9] to-white dark:bg-none dark:bg-[#121212] py-20 px-6 md:px-16 lg:px-24 w-full overflow-hidden transition-colors duration-500"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: false, amount: 0.1 }}
      style={{ minHeight: '100vh' }}
    >
      <div className="absolute inset-0 z-0 pointer-events-none">
        <motion.div
          className={`absolute top-[10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-[#172b81]/12 dark:bg-[#172b81]/5 blur-[120px] transition-opacity duration-1000 ${selectedBrand ? 'opacity-40' : 'opacity-100'}`}
          style={{ y: brandBlobY1 }}
        />
        <motion.div
          className={`absolute bottom-[10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#e1ae48]/12 dark:bg-[#e1ae48]/5 blur-[120px] transition-opacity duration-1000 ${selectedBrand ? 'opacity-40' : 'opacity-100'}`}
          style={{ y: brandBlobY2 }}
        />
      </div>

      <AnimatePresence mode="wait">
        {!selectedBrand ? (
          <motion.div
            key="brands-selection"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20, transition: { duration: 0.4 } }}
            className="relative z-10 max-w-screen-2xl mx-auto flex flex-col h-full"
          >
            <motion.div
              className="text-center mb-10"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: false, amount: 0.4 }}
            >
              <p className="text-[10px] uppercase font-bold tracking-[0.5em] text-slate-400 mb-4">Curated Partnership</p>
              <AnimatedText
                as={motion.h2}
                className="text-5xl md:text-7xl font-black text-slate-900 dark:text-white tracking-tighter"
                segments={[
                  { text: "The" },
                  { text: "Cold Chain", className: "font-light italic text-[#172b81] dark:text-slate-500" },
                  { text: "Standard." },
                ]}
              />
            </motion.div>

            {/* --- MOBILE VIEW: Only visible on small screens --- */}
            <motion.div
              className="lg:hidden flex flex-col gap-6 mt-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, amount: 0.3 }}
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.15 } },
              }}
            >
              {/* Liebherr Mobile Card */}
              <motion.div
                variants={{ hidden: { opacity: 0, scale: 0.85 }, visible: { opacity: 1, scale: 1 } }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelectedBrand('liebherr')}
                className="bg-slate-50 dark:bg-white/5 p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 flex flex-col items-center text-center"
              >
                <h3 className="text-[10px] font-bold text-[#172b81] uppercase tracking-[0.4em] mb-2">Germany</h3>
                <h2 className="text-4xl font-black mb-4 dark:text-white">LIEBHERR</h2>
                <img src="./images/EFE 4602 PNG.webp" loading="lazy" decoding="async" className="h-40 object-contain mb-6" alt="Liebherr" />
                <span className="text-[10px] font-bold uppercase tracking-widest px-8 py-3 bg-[#172b81] text-white rounded-full">Explore Liebherr</span>
              </motion.div>

              {/* Arctique Mobile Card */}
              <motion.div
                variants={{ hidden: { opacity: 0, scale: 0.85 }, visible: { opacity: 1, scale: 1 } }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setSelectedBrand('arctique')}
                className="bg-slate-50 dark:bg-white/5 p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 flex flex-col items-center text-center"
              >
                <h3 className="text-[10px] font-bold text-[#e1ae48] uppercase tracking-[0.4em] mb-2">Asia Pacific</h3>
                <h2 className="text-4xl font-black mb-4 dark:text-white">ARCTIQUE</h2>
                <img src="./images/Open Meat Display Chiller.webp" loading="lazy" decoding="async" className="h-40 object-contain mb-6" alt="Arctique" />
                <span className="text-[10px] font-bold uppercase tracking-widest px-8 py-3 bg-[#e1ae48] text-black rounded-full">Explore Arctique</span>
              </motion.div>
            </motion.div>

            {/* --- DESKTOP VIEW: Hidden on mobile --- */}
            <div className="hidden lg:block relative h-[850px] w-full mt-4">
              <div className="flex justify-between items-start w-full relative z-20 pt-6">
                <motion.div
                  className="w-[28%] border-l-2 border-slate-300 dark:border-white/10 pl-10"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false, amount: 0.4 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3 className="text-[11px] font-bold text-[#172b81] uppercase tracking-[0.4em] mb-3">Germany</h3>
                  <h2 className="text-5xl font-black mb-6 text-slate-900 dark:text-white">LIEBHERR</h2>
                  <p className="text-slate-500 dark:text-slate-400 font-light leading-relaxed mb-10 text-base">
                    Precision cooling solutions engineered for the most demanding professional environments.
                  </p>
                  <CreativeButton
                    onMouseEnter={() => setHoveredBrand('liebherr')}
                    onMouseLeave={() => setHoveredBrand(null)}
                    onClick={() => setSelectedBrand('liebherr')}
                    fillColor="#e1ae48"
                    hoverTextClassName="text-black"
                    className="px-10 py-5 bg-[#172b81] text-white transition-transform hover:scale-105 active:scale-95 shadow-xl shadow-blue-900/20 cursor-pointer"
                  >
                    <span className="font-bold uppercase tracking-widest text-xs">Explore Liebherr</span>
                  </CreativeButton>
                </motion.div>

                <motion.div
                  className="w-[28%] text-right border-r-2 border-slate-300 dark:border-white/10 pr-10"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: false, amount: 0.4 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3 className="text-[11px] font-bold text-[#e1ae48] uppercase tracking-[0.4em] mb-3">Asia Pacific</h3>
                  <h2 className="text-5xl font-black mb-6 text-slate-900 dark:text-white">ARCTIQUE</h2>
                  <p className="text-slate-500 dark:text-slate-400 font-light leading-relaxed mb-10 text-base">
                    Bespoke aesthetics meet high-performance cold chain innovation for retail display.
                  </p>
                  <CreativeButton
                    onMouseEnter={() => setHoveredBrand('arctique')}
                    onMouseLeave={() => setHoveredBrand(null)}
                    onClick={() => setSelectedBrand('arctique')}
                    fillColor="#172b81"
                    hoverTextClassName="text-white"
                    className="px-10 py-5 bg-[#e1ae48] text-black transition-transform hover:scale-105 active:scale-95 shadow-xl shadow-yellow-600/20 cursor-pointer ml-auto"
                  >
                    <span className="font-bold uppercase tracking-widest text-xs">Explore Arctique</span>
                  </CreativeButton>
                </motion.div>
              </div>

              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 pt-40">
                <div className="relative flex items-end justify-center">
                  <motion.img
                    src="./images/EFE 4602 PNG.webp"
                    alt="Liebherr"
                    loading="lazy"
                    decoding="async"
                    className="h-[320px] md:h-[380px] object-contain transition-all duration-500"
                    animate={{
                      scale: hoveredBrand === 'liebherr' ? 1.12 : 1,
                      filter: hoveredBrand === 'arctique' ? 'grayscale(1) opacity(0.3)' : 'none'
                    }}
                    style={{ transform: "scaleX(-1) rotateY(15deg)", marginRight: '-20px' }}
                  />
                  <motion.img
                    src="./images/Open Meat Display Chiller.webp"
                    alt="Arctique"
                    loading="lazy"
                    decoding="async"
                    className="h-[380px] md:h-[420px] object-contain transition-all duration-500"
                    animate={{
                      scale: hoveredBrand === 'arctique' ? 1.12 : 1,
                      filter: hoveredBrand === 'liebherr' ? 'grayscale(1) opacity(0.3)' : 'none'
                    }}
                    style={{ marginLeft: '-20px' }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="catalogue"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40, transition: { duration: 0.4 } }}
            className="max-w-screen-2xl mx-auto pt-4 relative z-10"
          >
            <div className="flex justify-between items-end mb-12 border-b border-slate-300 dark:border-white/5 pb-8">
              <div>
                <button
                  onClick={() => { setSelectedBrand(null); setBrandCatalogIndex(0); }}
                  className="mb-6 font-bold text-[10px] tracking-[0.3em] text-slate-400 hover:text-[#172b81] dark:hover:text-white uppercase flex items-center gap-2 transition-colors cursor-pointer"
                >
                  <KeyboardArrowLeftIcon fontSize="small" /> Back to brands
                </button>
                <h3 className="text-4xl md:text-6xl font-black uppercase dark:text-white tracking-tighter">
                  {selectedBrand.charAt(0).toUpperCase() + selectedBrand.slice(1)} <span className="font-light text-[#172b81] dark:text-slate-700">Series</span>
                </h3>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 h-auto lg:h-[650px] w-full items-stretch">
              {brandData[selectedBrand]!.map((item, index) => {
                const isExpanded = brandCatalogIndex === index;
                const accentColor = selectedBrand === 'liebherr' ? '#172b81' : '#E1AE48';

                // Detection for Dark Mode
                const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

                return (
                  <motion.div
                    key={index}
                    onMouseEnter={() => window.innerWidth >= 1024 && setBrandCatalogIndex(index)}
                    onClick={() => setBrandCatalogIndex(index)}
                    animate={{
                      flex: isExpanded ? 4 : 1.2,
                      minHeight: isExpanded ? '400px' : '100px' // Helps mobile tap visibility
                    }}
                    transition={{ type: "spring", stiffness: 120, damping: 20 }}
                    className={`relative flex flex-col justify-between overflow-hidden rounded-[2.5rem] p-10 transition-all duration-700 cursor-pointer border
                      ${isExpanded
                        ? 'bg-slate-50 dark:bg-white/[0.03] border-slate-300 dark:border-white/10 shadow-2xl shadow-black/5'
                        : 'bg-transparent border-slate-100 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/20'}
                    `}
                  >
                    <div className="flex justify-between items-start z-20">
                      <span className={`text-[10px] font-black tracking-[0.2em] transition-colors duration-500 ${isExpanded ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}>
                        0{index + 1} // {selectedBrand.toUpperCase()}
                      </span>
                      <div className={`w-8 h-8 rounded-full border border-slate-300 dark:border-white/10 flex items-center justify-center transition-all duration-500 ${isExpanded ? 'rotate-45 scale-110' : 'opacity-50'}`}>
                        <span className="text-lg font-light text-slate-400">+</span>
                      </div>
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 px-6">
                      <div className="relative flex items-center justify-center">
                        {/* Shadow for Dark Mode Floor Effect (Retained from original) */}
                        <div className={`
                          hidden dark:block
                          absolute bottom-[-30px]
                          w-[65%] h-[28px]
                          bg-black/60
                          blur-2xl
                          opacity-60
                          transition-all duration-700
                          ${isExpanded ? 'scale-100' : 'scale-75'}
                        `} />

                        <motion.img
                          src={item.image}
                          alt={item.title}
                          loading="lazy"
                          decoding="async"
                          transition={{ type: "spring", stiffness: 100, damping: 25 }}
                          animate={{
                            y: isExpanded ? -60 : -20,
                            scale: isExpanded ? 1 : 0.65,
                            opacity: isExpanded ? 1 : 0.25,
                            filter: isExpanded
                              ? (isDarkMode
                                  ? `brightness(0.95) contrast(0.92) saturate(0.9) drop-shadow(0 30px 45px rgba(0,0,0,0.45)) drop-shadow(0 0 60px ${accentColor}22)`
                                  : `brightness(1) contrast(1) saturate(1) drop-shadow(0 15px 25px rgba(0,0,0,0.08)) drop-shadow(0 0 40px ${accentColor}15)`)
                              : (isDarkMode
                                  ? `grayscale(1) blur(1px) brightness(0.8)`
                                  : `grayscale(1) blur(1px) brightness(0.95)`),
                          }}
                          className="relative max-h-[350px] w-auto object-contain transition-all duration-700"
                        />
                      </div>
                    </div>

                    <div className="mt-auto z-20">
                      <h4
                        className="text-2xl md:text-3xl font-black uppercase tracking-tight mb-2 transition-all duration-500"
                        style={{
                          color: isExpanded ? accentColor : 'inherit',
                          transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)'
                        }}
                      >
                        {item.title}
                      </h4>

                      <div className={`transition-all duration-700 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[300px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                        <p className="text-slate-600 dark:text-slate-400 text-sm font-light leading-relaxed mb-6 max-w-md">
                          {item.desc}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const categoryId = item.title.toLowerCase().includes('chest') ? 'chest' :
                                              item.title.toLowerCase().includes('upright') ? 'upright' :
                                              item.title.toLowerCase().includes('hard') ? 'hardtop' :
                                              item.title.toLowerCase().includes('supermarket') ? 'supermarket' : 'all';

                            window.scrollTo({ top: 0, behavior: 'smooth' });

                            setTimeout(() => {
                              navigate('/products', {
                                state: {
                                  filterBrand: selectedBrand,
                                  filterCategory: categoryId
                                }
                              });
                            }, 400);
                          }}
                          className="group flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest border-b-2 pb-1 transition-colors"
                          style={{ borderColor: accentColor, color: accentColor }}
                        >
                          View Catalog <ArrowOutwardIcon className="w-3 h-3 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default Brands;
